"use client";

import { motion } from "framer-motion";
import { Upload, Link2, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { ThemeSection, ThemeText, AuroraBlobs } from "@/components/ui/theme-utils";

const STEPS = [
  {
    n: "01",
    title: "Drop your file",
    description: "Drag it in — Zset chunks it on-device.",
    icon: Upload
  },
  {
    n: "02",
    title: "Get a smart link",
    description: "Share with expiry, password, or view-only.",
    icon: Link2
  },
  {
    n: "03",
    title: "Sit back",
    description: "Auto-retry and resume, on autopilot.",
    icon: Sparkles
  }
];

export function HowItWorks() {
  const { isDark } = useTheme();

  return (
    <ThemeSection id="how-it-works" variant="alternate" pattern="grid">
      <AuroraBlobs variant="cyan" />
      <div className="container mx-auto px-5 sm:px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-14 sm:mb-20">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium mb-5",
              isDark
                ? "bg-white/[0.04] border border-white/10 text-white/80"
                : "bg-white border border-stone-200 text-stone-700"
            )}
          >
            <Sparkles className="h-3.5 w-3.5 text-brand-500" />
            How it works
          </span>
          <h2 className="responsive-heading-2 mb-5">
            Three steps. <span className="text-gradient">Zero babysitting.</span>
          </h2>
          <ThemeText variant="subtle" className="responsive-text-lg">
            The full transfer loop, designed so you only touch it once.
          </ThemeText>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 relative">
          {/* connecting line */}
          <div
            className={cn(
              "hidden md:block absolute top-12 left-[16%] right-[16%] h-px -z-0",
              isDark
                ? "bg-gradient-to-r from-transparent via-white/15 to-transparent"
                : "bg-gradient-to-r from-transparent via-stone-300 to-transparent"
            )}
          />

          {STEPS.map((step, i) => (
            <motion.div
              key={step.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="relative"
            >
              <div
                className={cn(
                  "relative rounded-3xl p-7 h-full",
                  isDark
                    ? "bg-white/[0.02] border border-white/[0.08]"
                    : "bg-white border border-stone-200/80"
                )}
              >
                <div className="flex items-center justify-between mb-6">
                  <span
                    className={cn(
                      "inline-flex h-12 w-12 items-center justify-center rounded-2xl",
                      "bg-gradient-to-br from-brand-700 to-brand-900 text-white shadow-glow-sm",
                      "ring-1 ring-inset ring-white/10"
                    )}
                  >
                    <step.icon className="h-5 w-5" />
                  </span>
                  <span
                    className={cn(
                      "text-xs font-mono tracking-wider",
                      isDark ? "text-white/30" : "text-stone-400"
                    )}
                  >
                    {step.n}
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-bold tracking-tight mb-2">
                  {step.title}
                </h3>
                <ThemeText variant="subtle" className="text-sm">
                  {step.description}
                </ThemeText>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </ThemeSection>
  );
}
